import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { SearchInputComponent } from '@libs/common/components';
import { GtinHelper, Gtin } from '@libs/shared/models';
import { LMIV_SEARCH_QUERY_PARAM } from '@libs/shared/models';
import { ViewportService } from '@libs/shared/services';
import { LmivNavigationService } from '../../lmiv-navigation.service';

export const LmivProductSearchErrorDescriptions: Record<string, string> = {
  required: 'Bitte eine Artikelnummer eingeben.',
  pattern: 'Nur Ziffern erlaubt.',
  length: 'Die Länge der Artikelnummer ist ungültig.',
  checkDigit: 'Die Prüfziffer der Artikelnummer ist ungültig.',
};

const gtinValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = typeof control.value === 'string' ? control.value.trim() : '';

  if (!value) {
    return { required: true };
  }

  if (!GtinHelper.isNumeric(value)) {
    return { pattern: true };
  }

  const gtin: Gtin | null = GtinHelper.validate(value);

  if (!gtin) {
    return { length: true };
  }

  if (!gtin.isValid) {
    return { checkDigit: true };
  }

  return null;
};

const validators: ValidatorFn[] = [ gtinValidator ];

@Component({
  selector: 'ui-lmiv-product-search',
  templateUrl: './lmiv-product-search.component.html',
  styleUrls: ['./lmiv-product-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    SearchInputComponent,
  ],
  host: {
    '[class.mobile]': `isMobile()`,
  },
})
export class LmivProductSearchComponent {
  public readonly searchText: InputSignal<string> = input<string>('');

  protected readonly validators: ValidatorFn[] = validators;
  protected readonly errors: WritableSignal<ValidationErrors | null> = signal(null);
  protected readonly isMobile: Signal<boolean> = toSignal(inject(ViewportService).isMobile(), { initialValue: false });

  protected readonly errorMessage: Signal<string | null> = computed(() => {
    const errors: ValidationErrors | null = this.errors();

    if (!errors) {
      return null;
    }

    for (const errorType of ['pattern', 'length', 'checkDigit']) {
      if (errors[errorType]) {
        return LmivProductSearchErrorDescriptions[errorType];
      }
    }

    return null;
  });

  private readonly router: Router = inject(Router);
  private readonly navigation: LmivNavigationService = inject(LmivNavigationService);

  public search(value: string): void {
    if (this.errors() !== null) {
      return;
    }

    void this.router.navigate([this.navigation.routePath()], {
      queryParams: { [LMIV_SEARCH_QUERY_PARAM]: GtinHelper.normalize(value) },
    });
  }
}
